/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import i18n from 'i18next';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

import { registerField } from 'core/forms';
import { addToast, handleAxiosError } from 'core/notifications';

import { Button } from 'uikit/buttons';

import FileListItem from '@/exceed/components/fileListItem';
import useGlobalQueries from '@/exceed/hooks/useGlobalQueries';
import useIsFeatureEnabled from '@/exceed/hooks/useIsFeatureEnabled';
import useLoadScript from '@/exceed/hooks/useLoadScript';
import usePreviousState from '@/exceed/hooks/usePreviousState';

import { fileUploader } from '@/files';

import getAdminCloudSettings from '../helpers/getAdminCloudSettings';
import getFilesToRender from '../helpers/getFilesToRender';
import handleSourceMaterialMaxFileSizeError from '../helpers/handleSourceMaterialMaxFileSizeError';
import trackSourceMaterialFileUploadSize from '../helpers/trackSourceMaterialFileUploadSize';

import styles from '../styles/formFieldOneDrive.module.scss';

const PDF_SUPPORTED_FORMAT = [
  'doc',
  'docx',
  'epub',
  'eml',
  'htm',
  'html',
  'md',
  'msg',
  'odp',
  'ods',
  'odt',
  'pps',
  'ppsx',
  'ppt',
  'pptx',
  'rtf',
  'tif',
  'tiff',
  'xls',
  'xlsm',
  'xlsx',
];

const downloadFile = async ({ accessToken, apiEndpoint, id, name, url }) => {
  const [fileName, fileExtension] = name.split('.');

  if (PDF_SUPPORTED_FORMAT.includes(fileExtension.toLowerCase())) {
    const downloadUrl = `${apiEndpoint}/drive/items/${id}/content?format=pdf`;
    const file = await axios
      .get(downloadUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: 'blob',
      })
      .then(
        (response) => new File([response.data], `${fileName}.pdf`, { type: 'application/pdf' }),
      );

    return file;
  }

  const file = await axios
    .get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      responseType: 'blob',
    })
    .then((response) => new File([response.data], name, { type: response.data.type }));

  return file;
};

const FormFieldOneDrive = ({
  fieldKey,
  fieldId,
  schema: { fileTypes = [], maxUploadSizeInMb = 500, setIsFileUploading, multiSelect = true },
  updateField,
  value = [],
}) => {
  const [uploadMap, setUploadMap] = useState({});
  const prevUploadMap = usePreviousState(uploadMap);
  const files = useMemo(() => getFilesToRender({ uploadMap, value }), [uploadMap, value]);
  const { adminSettings } = useGlobalQueries();
  const cloud_settings = getAdminCloudSettings(adminSettings);
  const one_drive_client_id = cloud_settings.file_picker_settings?.one_drive?.client_id;
  const hasOneDriveFeature = useIsFeatureEnabled('enable_one_drive_file_picker_integration');
  const oneDriveEnabled = hasOneDriveFeature && one_drive_client_id;

  useLoadScript({
    url: 'https://js.live.net/v7.2/OneDrive.js',
    options: { defer: true },
  });

  useEffect(() => {
    const completedUploads = Object.values(uploadMap).filter(({ status }) => status === 'complete');

    if (completedUploads.length > 0) {
      const newFiles = completedUploads.map(({ file }) => file);

      setUploadMap((map) => {
        const newUploadMap = { ...map };

        completedUploads.forEach(({ file }) => {
          delete newUploadMap[file.name];
        });

        return newUploadMap;
      });

      updateField(sortBy([...value, ...newFiles], 'name'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateField, uploadMap]);

  useEffect(() => {
    if ((!prevUploadMap || !Object.keys(prevUploadMap).length) && Object.keys(uploadMap).length) {
      setIsFileUploading(true);
    } else if (
      prevUploadMap &&
      Object.keys(prevUploadMap).length &&
      !Object.keys(uploadMap).length
    ) {
      setIsFileUploading(false);
    }
  }, [prevUploadMap, setIsFileUploading, uploadMap]);

  const openPicker = () => {
    const current_domain = window.location.origin;
    const options = {
      clientId: one_drive_client_id,
      action: 'download',
      multiSelect,
      openInNewWindow: true,
      advanced: {
        redirectUri: `${current_domain}/file_picker/one_drive`,
        ...(fileTypes && { filter: fileTypes.join(',') }),
      },
      success: async ({ accessToken, apiEndpoint, value: docs }) => {
        try {
          const downloadRequests = docs.map(
            async ({ id, name, size, '@microsoft.graph.downloadUrl': url, ...metadata }) => {
              const file = await downloadFile({ accessToken, apiEndpoint, id, name, url });

              if (
                files.some(
                  ({ name: fileName, resource }) =>
                    resource?.external_id === id || fileName === file.name,
                )
              ) {
                addToast(i18n.t('file-already-exists'), 'error');
                return;
              }

              const maxFileSize =
                typeof maxUploadSizeInMb === 'number'
                  ? maxUploadSizeInMb
                  : maxUploadSizeInMb(file.type);

              if (file.size > maxFileSize * 1000000) {
                handleSourceMaterialMaxFileSizeError({ maxFileSize, mimeType: file.type });
                trackSourceMaterialFileUploadSize({
                  fileSize: file.size,
                  uploadStatus: 'failure',
                });
                return;
              }

              return {
                file,
                data: {
                  material_type: 'Resource',
                  import_type: fieldKey,
                  file_size: file.size,
                  name: file.name,
                  resource: {
                    external_id: id,
                    mime: file.type,
                    metadata,
                  },
                  status: 'created',
                },
              };
            },
          );

          await Promise.all(downloadRequests)
            .then(async (downloadedFiles) => {
              const uploadFileRequests = downloadedFiles
                .filter((file) => file)
                .map(({ file, data }) =>
                  fileUploader(file, {
                    start: () => {
                      setUploadMap((map) => ({
                        ...map,
                        [data.name]: {
                          status: 'uploading',
                          progress: 0,
                          file: data,
                        },
                      }));
                    },
                    progress: (percent) => {
                      if (percent !== 100) {
                        setUploadMap((map) => ({
                          ...map,
                          [data.name]: {
                            ...map[data.name],
                            progress: percent,
                          },
                        }));
                      }
                    },
                    complete: (completionObj) => {
                      const fileUpload = { ...data };
                      fileUpload.resource.url = completionObj.url || completionObj;

                      trackSourceMaterialFileUploadSize({
                        fileSize: file.size,
                        uploadStatus: 'success',
                      });
                      setUploadMap((map) => ({
                        ...map,
                        [data.name]: {
                          status: 'complete',
                          progress: 100,
                          file: data,
                        },
                      }));
                    },
                    error: (errorMessage) => {
                      setUploadMap((map) => {
                        const newMap = { ...map };

                        delete newMap[data.name];

                        return newMap;
                      });

                      addToast(errorMessage, 'error');
                    },
                  }),
                );

              await Promise.all(uploadFileRequests).catch((error) => {
                throw error;
              });
            })
            .catch((error) => {
              throw error;
            });
        } catch (error) {
          handleAxiosError(error);
        }
      },
      error: () => {
        addToast(i18n.t('oops-wrong'), 'error');
      },
    };

    window.OneDrive.open(options);
  };

  const onRemove = (removeFile) => {
    const filteredValue = value.filter((file) => !isEqual(file, removeFile));
    updateField(filteredValue);
  };

  if (!oneDriveEnabled) {
    return (
      <div id={fieldId}>
        <p
          className={styles.disabledMessage}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: i18n.t('file-picker-not-enabled') }}
        />
      </div>
    );
  }

  return (
    <div id={fieldId}>
      <ul className={styles.list}>
        {files.map((item, index) => {
          const { file_size, material_type, name } = item;
          const upload = uploadMap[name] || null;

          return (
            <FileListItem
              // eslint-disable-next-line react/no-array-index-key
              key={`source_material_one_drive_${index}`}
              name={name}
              fileSize={file_size}
              materialType={material_type}
              onRemove={() => onRemove(item)}
              upload={upload}
            />
          );
        })}
      </ul>
      <Button variant="outlined" onClick={openPicker}>
        {i18n.t('choose-file')}
      </Button>
    </div>
  );
};

FormFieldOneDrive.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    fileTypes: PropTypes.arrayOf(PropTypes.string),
    maxUploadSizeInMb: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
    multiSelect: PropTypes.bool,
    setIsFileUploading: PropTypes.func.isRequired,
  }).isRequired,
  updateField: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      material_type: PropTypes.oneOf(['Course', 'Resource']).isRequired,
      import_type: PropTypes.oneOf(['file_upload', 'google_drive', 'one_drive']),
      file_size: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      resource: PropTypes.shape({
        external_id: PropTypes.string,
        mime: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        metadata: PropTypes.shape({}),
      }),
      status: PropTypes.oneOf([
        'created',
        'failure',
        'rag_token_requested',
        'request_file_transcript',
        'requested',
        'success',
      ]).isRequired,
    }),
  ),
};

registerField('OneDrive', FormFieldOneDrive);

export default FormFieldOneDrive;
